@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_cus

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.example.project.dashboard.RegisterResourceSyncHandler
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularSplitScreen
import org.example.project.registrierung.RegistrierungApi
import org.example.project.service.CustomerApi
import org.example.project.service.GlobalManager
import org.example.project.utils.All
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Small
import org.jetbrains.compose.web.dom.Text

private data class CusVerbrauchModuleStats(
    val owned: Int = 0,
    val active: Int = 0
)

private fun loadCusVerbrauchModuleStats(entries: List<All>): CusVerbrauchModuleStats {
    val ownedRows = entries.filter { it.modulId > 0 && (it.shopPurchased || it.modulInstalliert) }
    return CusVerbrauchModuleStats(
        owned = ownedRows.size,
        active = ownedRows.count { it.modulInstalliert }
    )
}

private fun cusSnapshotValue(container: All, key: String): String =
    when (key) {
        "onboardingPreis" -> container.onboardingPreis
        "lizensierungPreis" -> container.lizensierungPreis
        "crStd" -> container.crStd
        "crKosten" -> container.crKosten
        "supportTickets" -> container.supportTickets
        "supportKosten" -> container.supportKosten
        "meetingStd" -> container.meetingStd
        "meetingKosten" -> container.meetingKosten
        "schulungStd" -> container.schulungStd
        "schulungKosten" -> container.schulungKosten
        "beratungStd" -> container.beratungStd
        "beratungKosten" -> container.beratungKosten
        "gesamtbetragEuro" -> container.gesamtbetragEuro
        else -> ""
    }

private fun cusDetailTypLabel(row: All): String = when {
    row.notiz.startsWith("AUTO_ONBOARDING_SETUP") -> "Onboarding"
    row.notiz.startsWith("AUTO_LIZENZ_AKTIV") -> "Lizenzierung"
    row.ticketnummer.isNotBlank() || row.notiz.startsWith("AUTO_SUPPORT_TICKET:") -> "Support"
    row.notiz.startsWith("AUTO_SERVICE_TERMIN:") -> row.terminArt.ifBlank { "Service" }
    row.notiz.isNotBlank() -> row.notiz
    else -> "Manuell"
}

@Composable
fun CUSKundenverbrauch() {
    val scope = rememberCoroutineScope()
    var kunde by remember { mutableStateOf<All?>(null) }
    var verbrauchContainer by remember { mutableStateOf(All()) }
    var kopfGesucht by remember { mutableStateOf(false) }
    var moduleStats by remember { mutableStateOf(CusVerbrauchModuleStats()) }
    val monthKey = remember {
        val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
        "${now.year}_${now.monthNumber.toString().padStart(2, '0')}"
    }

    fun detailRowKey(row: All): String = if (row.ticketnummer.isNotBlank()) "SUP:${row.id}" else "VRB:${row.id}"

    fun selectedRows(): List<All> =
        verbrauchContainer.verbrauch
            .sortedWith(compareBy<All> { it.datum }.thenBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }.thenBy { it.id })

    fun detailRowsForTable(): List<Pair<String, All>> =
        selectedRows().mapIndexed { index, row ->
            detailRowKey(row) to row.copy(label = (index + 1).toString())
        }

    fun mapKundeRow(currentKunde: All): List<String> = listOf(
        verbrauchContainer.id.ifBlank { "${currentKunde.firmenId}_$monthKey" },
        currentKunde.firmenId,
        currentKunde.firma,
        currentKunde.lizenzkey,
        monthKey,
        cusSnapshotValue(verbrauchContainer, "onboardingPreis"),
        cusSnapshotValue(verbrauchContainer, "lizensierungPreis"),
        cusSnapshotValue(verbrauchContainer, "crStd"),
        cusSnapshotValue(verbrauchContainer, "crKosten"),
        cusSnapshotValue(verbrauchContainer, "supportTickets"),
        cusSnapshotValue(verbrauchContainer, "supportKosten"),
        cusSnapshotValue(verbrauchContainer, "meetingStd"),
        cusSnapshotValue(verbrauchContainer, "meetingKosten"),
        cusSnapshotValue(verbrauchContainer, "schulungStd"),
        cusSnapshotValue(verbrauchContainer, "schulungKosten"),
        cusSnapshotValue(verbrauchContainer, "beratungStd"),
        cusSnapshotValue(verbrauchContainer, "beratungKosten"),
        cusSnapshotValue(verbrauchContainer, "gesamtbetragEuro")
    )

    fun mapDetailRow(row: All): List<String> = listOf(
        row.label.ifBlank { row.id },
        row.datum,
        cusDetailTypLabel(row),
        row.einmalzahlung,
        row.lizenzMonatlich,
        row.crSatz,
        if (row.ticketnummer.isNotBlank()) "1" else row.supportTicket,
        row.meetings,
        row.extraSchulungRemote,
        row.sonstiges
    )

    suspend fun reload() {
        val companies = RegistrierungApi.loadCompanies()
        val nodeStatus = try {
            window.fetch("/node/status").then { it.json() }.await()
        } catch (_: Throwable) {
            null
        }
        val nodeFirmenId = nodeStatus?.asDynamic()?.firmenId as? String ?: ""
        val resolvedFirmenId = GlobalManager.currentFirmenId.ifBlank { nodeFirmenId }

        val currentKunde = companies
            .filter { it.istkunde }
            .sortedBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }
            .firstOrNull {
                resolvedFirmenId.isNotBlank() && it.firmenId == resolvedFirmenId
            }

        kunde = currentKunde?.let { base ->
            val setup = base.firmenId
                .takeIf { it.isNotBlank() }
                ?.let { CustomerApi.loadDetails(it) }

            base.copy(
                lizenzkey = setup?.lizenzkey.orEmpty(),
                nodePort = setup?.nodePort.orEmpty(),
                zustand = setup?.zustand ?: base.zustand
            )
        }

        val resolvedKunde = kunde ?: return
        verbrauchContainer = All(
            id = "${resolvedKunde.firmenId}_$monthKey",
            firmenId = resolvedKunde.firmenId,
            firma = resolvedKunde.firma,
            datum = monthKey,
            verbrauch = emptyList()
        )
        moduleStats = CusVerbrauchModuleStats()
    }

    LaunchedEffect(Unit) {
        reload()
    }

    RegisterResourceSyncHandler("CUSKundenverbrauch") { event ->
        if (event.group != "verbrauch") return@RegisterResourceSyncHandler
        if (event.resourceType !in setOf("customer-consumption", "customer-invoice", "billing-settings")) {
            return@RegisterResourceSyncHandler
        }
        val activeFirmenId = kunde?.firmenId.orEmpty()
        if (event.companyId.isNotBlank() && activeFirmenId.isNotBlank() && event.companyId != activeFirmenId) {
            return@RegisterResourceSyncHandler
        }
        scope.launch { reload() }
    }

    val currentKunde = kunde
    if (currentKunde == null) {
        Div { Text("Kein Kunde gefunden.") }
        return
    }

    TabularSplitScreen(
        initialRatio = 0.56f,
        screen1 = {
            TabularScreen(
                screenKey = "Kundenverbrauch",
                rollenKey = "Kundenverbrauch",
                title = "Verbrauch ($monthKey)",
                headers = listOf(
                    "ID",
                    "FirmenId",
                    "Firma",
                    "Lizenzkey",
                    "Monat",
                    "Onboarding Preis",
                    "Lizensierung Preis",
                    "CR STD",
                    "CR Kosten",
                    "SUP-Ticket",
                    "Support Kosten",
                    "Meeting STD",
                    "Meeting Kosten",
                    "Schulung STD",
                    "Schulung Kosten",
                    "Beratung STD",
                    "Beratung Kosten",
                    "Gesamt EUR"
                ),
                data = if (kopfGesucht) listOf(currentKunde.id to mapKundeRow(currentKunde)) else emptyList(),
                editable = false,
                includeMetadata = true,
                allowCreate = false,
                allowDelete = false,
                allowSave = false,
                onSearchWithFilters = { filters ->
                    scope.launch {
                        kopfGesucht = true
                        CustomerApi.searchVerbrauch(monthKey, filters).firstOrNull()?.let { filtered ->
                            verbrauchContainer = filtered
                        }
                    }
                },
                onCommit = {}
            )
        },
        screen2 = {
            TabularScreen(
                screenKey = "Kundenverbrauch_${currentKunde.id}_$monthKey",
                rollenKey = "Kundenverbrauch",
                title = "Details ${currentKunde.firma.ifBlank { currentKunde.firmenId }}",
                headers = listOf("ID", "Datum", "Typ", "Onboarding", "Lizensierung", "CR STD", "SUP-Ticket", "Meetings", "Schulung", "Beratung"),
                data = detailRowsForTable().map { (key, row) -> key to mapDetailRow(row) },
                editable = false,
                includeMetadata = true,
                allowCreate = false,
                allowDelete = false,
                allowSave = false,
                nonEditableCols = setOf("ID"),
                onSearchWithFilters = { filters ->
                    scope.launch {
                        verbrauchContainer = verbrauchContainer.copy(
                            verbrauch = CustomerApi.searchVerbrauchDetails(monthKey, filters)
                        )
                    }
                },
                onCommit = {}
            )
            Small {
                Text("Nur Lesemodus. Geladen aus SYS und auf deinen Kunden gefiltert.")
            }
        }
    )
}
